export interface CSVExportOptions {
  filename?: string;
  includeHeaders?: boolean;
  dateFormat?: 'iso' | 'locale';
}

export class CSVExporter {
  static escapeCSVField(field: any): string {
    if (field === null || field === undefined) {
      return '';
    }
    
    const stringField = String(field);
    
    // If field contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    
    return stringField;
  }

  static arrayToCSV(data: any[], options: CSVExportOptions = {}): string {
    if (!data || data.length === 0) {
      return '';
    }

    const { includeHeaders = true, dateFormat = 'iso' } = options;
    const headers = Object.keys(data[0]);
    const csvRows: string[] = [];

    // Add headers if requested
    if (includeHeaders) {
      csvRows.push(headers.map(header => this.escapeCSVField(header)).join(','));
    }

    // Add data rows
    data.forEach(row => {
      const csvRow = headers.map(header => {
        let value = row[header];
        
        // Format dates if needed
        if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
          if (dateFormat === 'locale') {
            value = new Date(value).toLocaleString();
          }
        }
        
        return this.escapeCSVField(value);
      }).join(',');
      
      csvRows.push(csvRow);
    });

    return csvRows.join('\n');
  }

  static downloadCSV(csvContent: string, filename: string = 'export.csv'): void {
    // Create blob with CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
  }

  static async exportTableToCSV(
    tableName: string, 
    supabaseClient: any, 
    options: CSVExportOptions = {}
  ): Promise<{ success: boolean; error?: string; filename?: string }> {
    try {
      const { filename = `${tableName}_export_${new Date().toISOString().split('T')[0]}.csv` } = options;
      
      // Fetch all data from the table
      const { data, error } = await supabaseClient
        .from(tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        return { success: false, error: 'No data found to export' };
      }

      // Convert to CSV
      const csvContent = this.arrayToCSV(data, options);
      
      // Download the file
      this.downloadCSV(csvContent, filename);
      
      return { success: true, filename };
    } catch (error) {
      console.error('CSV export error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to export data' 
      };
    }
  }
}